/**
 * Touch Target Configuration
 * Ensures interactive elements meet accessibility requirements
 *
 * WCAG Reference:
 * - 2.5.5 Target Size (Level AAA): 44x44px minimum
 * - 2.5.8 Target Size (Minimum) (Level AA): 24x24px minimum with spacing
 *
 * Design Decision: We target 44x44px for all touch targets (AAA level)
 * to ensure optimal usability on touch devices.
 */

import { SxProps, Theme } from '@mui/material/styles';

/**
 * Minimum touch target size (44x44px per WCAG 2.5.5 AAA)
 */
export const MIN_TOUCH_TARGET = 44;

/**
 * Minimum spacing between touch targets (8px)
 */
export const MIN_TOUCH_SPACING = 8;

/**
 * Alternative minimum for AA compliance (24x24px)
 * Use only when 44px is not feasible
 */
export const MIN_TOUCH_TARGET_AA = 24;

/**
 * Touch target styles for ensuring minimum size
 * Apply to any interactive element
 */
export const touchTargetStyles: SxProps<Theme> = {
  minWidth: MIN_TOUCH_TARGET,
  minHeight: MIN_TOUCH_TARGET,
};

/**
 * Invisible touch target expansion
 * Creates a larger hit area without changing visual size
 * Uses pseudo-element for expanded touch area
 */
export const expandedTouchTarget: SxProps<Theme> = {
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: MIN_TOUCH_TARGET,
    height: MIN_TOUCH_TARGET,
    minWidth: MIN_TOUCH_TARGET,
    minHeight: MIN_TOUCH_TARGET,
  },
};

/**
 * Touch target styles with spacing
 * Includes margin for spacing between targets
 */
export const touchTargetWithSpacing: SxProps<Theme> = {
  minWidth: MIN_TOUCH_TARGET,
  minHeight: MIN_TOUCH_TARGET,
  margin: `${MIN_TOUCH_SPACING / 2}px`,
};

/**
 * Creates expanded touch target styles for icon buttons
 * Visually the button can be smaller, but the touch area is 44x44
 *
 * @example
 * ```tsx
 * <IconButton sx={iconButtonTouchTarget}>
 *   <CloseIcon />
 * </IconButton>
 * ```
 */
export const iconButtonTouchTarget: SxProps<Theme> = {
  // Ensure minimum touch area
  minWidth: MIN_TOUCH_TARGET,
  minHeight: MIN_TOUCH_TARGET,
  // Center the icon within the touch area
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Padding to maintain visual spacing
  padding: '10px',
};

/**
 * Compact touch target for dense layouts
 * Uses expanded pseudo-element for touch area
 * Visual size can be smaller than 44px
 */
export const compactTouchTarget: SxProps<Theme> = {
  position: 'relative',
  // Visual size can be smaller
  width: 32,
  height: 32,
  // Expanded touch area via pseudo-element
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: MIN_TOUCH_TARGET,
    height: MIN_TOUCH_TARGET,
    // Make it clickable but invisible
    pointerEvents: 'auto',
  },
};

/**
 * Link touch target styles
 * Ensures links have adequate touch area with padding
 */
export const linkTouchTarget: SxProps<Theme> = {
  display: 'inline-block',
  minHeight: MIN_TOUCH_TARGET,
  padding: '10px 4px',
  // Negative margin to maintain visual alignment
  margin: '-10px -4px',
};

/**
 * Form input touch target styles
 * Ensures form inputs are easily tappable
 */
export const inputTouchTarget: SxProps<Theme> = {
  minHeight: MIN_TOUCH_TARGET,
  '& .MuiInputBase-input': {
    minHeight: MIN_TOUCH_TARGET,
    padding: '10px 14px',
  },
};

/**
 * Checkbox/Radio touch target styles
 * Expands touch area for small controls
 */
export const controlTouchTarget: SxProps<Theme> = {
  padding: '10px',
  '& .MuiSvgIcon-root': {
    fontSize: 24,
  },
};

/**
 * Close button touch target (for modals, dialogs)
 * Ensures close buttons are easy to tap
 */
export const closeButtonTouchTarget: SxProps<Theme> = {
  position: 'absolute',
  top: 8,
  right: 8,
  minWidth: MIN_TOUCH_TARGET,
  minHeight: MIN_TOUCH_TARGET,
  padding: '10px',
};

/**
 * Touch-friendly button group spacing
 */
export const buttonGroupSpacing: SxProps<Theme> = {
  gap: `${MIN_TOUCH_SPACING}px`,
  '& .MuiButton-root': {
    minHeight: MIN_TOUCH_TARGET,
  },
};

/**
 * Navigation item touch target
 */
export const navItemTouchTarget: SxProps<Theme> = {
  minHeight: MIN_TOUCH_TARGET,
  paddingTop: '10px',
  paddingBottom: '10px',
};

/**
 * List item touch target
 */
export const listItemTouchTarget: SxProps<Theme> = {
  minHeight: MIN_TOUCH_TARGET,
  paddingTop: '10px',
  paddingBottom: '10px',
};

/**
 * Tab touch target
 */
export const tabTouchTarget: SxProps<Theme> = {
  minHeight: 48, // Standard MUI tab height
  minWidth: MIN_TOUCH_TARGET,
  padding: '12px 16px',
};

/**
 * Chip touch target (for clickable chips)
 */
export const chipTouchTarget: SxProps<Theme> = {
  minHeight: 32,
  // Expanded touch area via padding
  padding: '6px 12px',
  '& .MuiChip-deleteIcon': {
    minWidth: 24,
    minHeight: 24,
    margin: '0 4px 0 -4px',
  },
};

/**
 * Helper function to create custom touch target styles
 */
export function createTouchTarget(options: {
  minSize?: number;
  spacing?: number;
  expanded?: boolean;
}): SxProps<Theme> {
  const { minSize = MIN_TOUCH_TARGET, spacing = 0, expanded = false } = options;

  if (expanded) {
    return {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: minSize,
        height: minSize,
      },
      ...(spacing > 0 && { margin: `${spacing / 2}px` }),
    };
  }

  return {
    minWidth: minSize,
    minHeight: minSize,
    ...(spacing > 0 && { margin: `${spacing / 2}px` }),
  };
}

/**
 * Validates if an element meets touch target requirements
 * For use in tests and development tools
 */
export function validateTouchTarget(
  width: number,
  height: number,
  level: 'AA' | 'AAA' = 'AAA'
): { valid: boolean; message: string } {
  const minSize = level === 'AAA' ? MIN_TOUCH_TARGET : MIN_TOUCH_TARGET_AA;

  if (width >= minSize && height >= minSize) {
    return { valid: true, message: `Touch target meets WCAG ${level} (${width}x${height}px)` };
  }

  return {
    valid: false,
    message: `Touch target too small: ${width}x${height}px (minimum: ${minSize}x${minSize}px for WCAG ${level})`,
  };
}

/**
 * Validates spacing between touch targets
 */
export function validateTouchSpacing(spacing: number): { valid: boolean; message: string } {
  if (spacing >= MIN_TOUCH_SPACING) {
    return { valid: true, message: `Spacing adequate: ${spacing}px` };
  }

  return {
    valid: false,
    message: `Insufficient spacing: ${spacing}px (minimum: ${MIN_TOUCH_SPACING}px)`,
  };
}
